import { useEffect, useMemo, useState, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Collapsible from '../../components/ui/Collapsible.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Field, Input, Select, Textarea } from '../../components/ui/Field.jsx'
import { Modal } from '../../components/ui/Modal.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useCompany } from '../../context/CompanyContext.jsx'
import api from '../../lib/api.js'
import { fetchAllPaginated } from '../../lib/paginate.js'
import { formatCurrency } from '../../lib/format.js'
import { invoiceTotals, lineTotals } from './invoiceTotals.js'
import ArticleSearchModal from './ArticleSearchModal.jsx'
import PartyFormModal from '../parties/PartyFormModal.jsx'
import { PARTY_CONFIG } from '../parties/partyConfig.js'

/*
 * Invoice form — inventory §3. Create (and edit drafts) of outgoing invoices.
 * Draft vs finalize share POST /invoices/ via the `is_draft` flag; finalize
 * first fetches the next number and shows a confirmation modal. All feedback
 * goes through internal modals/toasts (DESIGN.md §8).
 */
let lineKey = 0
const newLine = (over = {}) => ({
  key: ++lineKey, description: '', quantity: 1, unit_price: 0, vat_rate_id: null,
  note: '', showNote: false,
  allowance_charge_amount: '', allowance_charge_reason: '', allowance_charge_is_percentage: false, is_charge: false, showAllowance: false,
  period_start: '', period_end: '', showPeriod: false,
  order_ref: '', doc_ref: '', showRef: false,
  ...over,
})

const today = () => new Date().toISOString().split('T')[0]
const REF_TYPES = ['ORDER', 'CONTRACT', 'BILLING', 'DESPATCH', 'RECEIPT', 'ORIGINATOR'] // backend ReferenceType enum

export default function InvoiceFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const seed = location.state?.seed // duplicate / credit-note prefill (inventory §3.1)
  const isEdit = !!id
  const { currentCompanyId } = useCompany()
  const { confirm, notify, toast } = useFeedback()

  const [customers, setCustomers] = useState([])
  const [vatRates, setVatRates] = useState([])
  const [bankAccounts, setBankAccounts] = useState([])
  const [nonVatLiable, setNonVatLiable] = useState(false)

  const [customerId, setCustomerId] = useState('')
  const [issueDate, setIssueDate] = useState(today())
  const [dueDate, setDueDate] = useState('')
  const [isCreditNote, setIsCreditNote] = useState(false)
  const [isPeppolSent, setIsPeppolSent] = useState(true)
  const [sendEmail, setSendEmail] = useState(true)

  const [paymentMethod, setPaymentMethod] = useState('bank_transfer')
  const [bankAccountId, setBankAccountId] = useState('')
  const [useStructuredComm, setUseStructuredComm] = useState(true)
  const [cardLast4, setCardLast4] = useState('')
  const [cardHolder, setCardHolder] = useState('')
  const [notes, setNotes] = useState('')
  const [docRefs, setDocRefs] = useState([]) // [{ reference_type, reference_id }]

  // Misc: invoicing period + delivery address (handoff "Informations diverses").
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [useBillingForDelivery, setUseBillingForDelivery] = useState(true)
  const [deliveryAddressId, setDeliveryAddressId] = useState('')
  const [deliveryAddresses, setDeliveryAddresses] = useState([])

  const [items, setItems] = useState([newLine()])
  const [articleTarget, setArticleTarget] = useState(null) // line key
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [finalize, setFinalize] = useState(null) // { nextNumber, totals }
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [attachments, setAttachments] = useState([])
  const [uploading, setUploading] = useState(false)

  // Attachments (edit mode): list + upload to the existing invoice.
  const loadAttachments = useCallback(() => {
    if (!isEdit) return
    api.get(`/invoices/${id}/attachments`).then(({ data }) => setAttachments(data?.attachments || [])).catch(() => {})
  }, [isEdit, id])
  useEffect(() => { loadAttachments() }, [loadAttachments])

  const uploadFile = async (file) => {
    if (!file) return
    setUploading(true)
    const fd = new FormData()
    fd.append('file', file)
    try { await api.post(`/invoices/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast(t('invoiceForm.attachments.uploaded'), 'success'); loadAttachments() }
    catch { toast(t('invoiceForm.attachments.uploadError'), 'error') } finally { setUploading(false) }
  }

  // Load reference data scoped to the active company.
  useEffect(() => {
    if (!currentCompanyId) return undefined
    let active = true
    setLoading(true)
    Promise.allSettled([
      fetchAllPaginated(api, '/customers/', 'customers'),
      api.get('/vat-rates/'),
      api.get(`/companies/${currentCompanyId}/bank-accounts`),
      api.get(`/companies/${currentCompanyId}`),
    ]).then(([cu, vr, ba, co]) => {
      if (!active) return
      if (cu.status === 'fulfilled') setCustomers(cu.value)
      if (vr.status === 'fulfilled') {
        const rates = vr.value.data?.vat_rates || []
        setVatRates(rates)
        // default new lines to the first rate (typically 21%)
        if (rates[0]) setItems((prev) => prev.map((l) => (l.vat_rate_id == null ? { ...l, vat_rate_id: rates[0].id } : l)))
      }
      if (ba.status === 'fulfilled') {
        const accts = ba.value.data?.bank_accounts || []
        setBankAccounts(accts)
        const def = accts.find((a) => a.is_default) || accts[0]
        if (def) setBankAccountId(String(def.id))
      }
      if (co.status === 'fulfilled') setNonVatLiable(!!(co.value.data?.company || co.value.data)?.non_vat_liable)
    }).finally(() => active && setLoading(false))
    return () => { active = false }
  }, [currentCompanyId])

  // Edit mode: load the existing invoice into the form. Only drafts are
  // editable (inventory §3.1) — finalized invoices bounce to the detail page.
  useEffect(() => {
    if (!isEdit) return undefined
    let active = true
    api.get(`/invoices/${id}`).then(({ data }) => {
      if (!active) return
      const inv = data?.invoice
      if (!inv) return
      if (!inv.is_draft) {
        notify({ tone: 'info', title: t('invoiceForm.cannotEdit.title'), message: t('invoiceForm.cannotEdit.message') })
          .then(() => navigate(`/app/invoices/${id}`))
        return
      }
      setCustomerId(inv.customer_id ? String(inv.customer_id) : '')
      setIssueDate((inv.issue_date || '').split('T')[0] || today())
      setDueDate((inv.due_date || '').split('T')[0] || '')
      setIsCreditNote(!!inv.is_credit_note)
      setIsPeppolSent(!!inv.is_peppol_sent)
      setSendEmail(!!inv.send_email_to_customer)
      setPaymentMethod(inv.payment_method || 'bank_transfer')
      setBankAccountId(inv.bank_account_id ? String(inv.bank_account_id) : '')
      setUseStructuredComm(inv.use_structured_communication !== false)
      setCardLast4(inv.payment_card_last_digits || '')
      setCardHolder(inv.payment_card_holder_name || '')
      setNotes(inv.notes || '')
      setPeriodStart((inv.billing_period_start_date || '').split('T')[0] || '')
      setPeriodEnd((inv.billing_period_end_date || '').split('T')[0] || '')
      setUseBillingForDelivery(inv.use_billing_address_for_delivery !== false)
      setDeliveryAddressId(inv.delivery_address_id ? String(inv.delivery_address_id) : '')
      setDocRefs((inv.document_references || []).map((r) => ({ reference_type: r.reference_type, reference_id: r.reference_id })))
      setItems((inv.items || []).map((it) => newLine({
        description: it.description || '', quantity: it.quantity ?? 1, unit_price: it.unit_price ?? 0,
        vat_rate_id: it.vat_rate_id ?? null, note: it.note || '', showNote: !!it.note,
        allowance_charge_amount: it.allowance_charge_amount ?? '', allowance_charge_reason: it.allowance_charge_reason || '',
        allowance_charge_is_percentage: !!it.allowance_charge_is_percentage, is_charge: !!it.is_charge,
        showAllowance: Number(it.allowance_charge_amount) > 0,
        period_start: (it.billing_period_start_date || '').split('T')[0] || '', period_end: (it.billing_period_end_date || '').split('T')[0] || '',
        showPeriod: !!it.billing_period_start_date, order_ref: it.order_line_reference || '', doc_ref: it.document_reference_id || '', showRef: !!(it.order_line_reference || it.document_reference_id),
      })))
    })
    return () => { active = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  // Duplicate / credit-note: prefill a NEW invoice from a seed passed via router
  // state. Credit notes auto-add a BILLING reference to the original invoice.
  useEffect(() => {
    if (isEdit || !seed) return
    if (seed.customer_id) setCustomerId(String(seed.customer_id))
    if (seed.is_credit_note) setIsCreditNote(true)
    if (Array.isArray(seed.items) && seed.items.length) {
      setItems(seed.items.map((it) => newLine({
        description: it.description || '', quantity: it.quantity ?? 1, unit_price: it.unit_price ?? 0,
        vat_rate_id: it.vat_rate_id ?? null, note: it.note || '', showNote: !!it.note,
        allowance_charge_amount: it.allowance_charge_amount ?? '', allowance_charge_reason: it.allowance_charge_reason || '',
        allowance_charge_is_percentage: !!it.allowance_charge_is_percentage, is_charge: !!it.is_charge,
        showAllowance: Number(it.allowance_charge_amount) > 0,
      })))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  // Delivery addresses for the selected customer (handoff "Adresse de livraison").
  useEffect(() => {
    if (!customerId) { setDeliveryAddresses([]); return }
    api.get(`/customers/${customerId}/delivery-addresses/`).then(({ data }) => setDeliveryAddresses(data?.delivery_addresses || data?.addresses || [])).catch(() => setDeliveryAddresses([]))
  }, [customerId])

  const ratePctOf = useCallback((line) => {
    const r = vatRates.find((v) => v.id === Number(line.vat_rate_id))
    return r ? r.rate : 0
  }, [vatRates])

  const totals = useMemo(() => invoiceTotals(items, ratePctOf), [items, ratePctOf])

  // Auto due date from the customer's payment terms when a customer is picked.
  const onCustomerChange = (value) => {
    setCustomerId(value)
    const c = customers.find((x) => String(x.id) === String(value))
    if (c?.payment_terms_days != null && issueDate) {
      const d = new Date(issueDate)
      d.setDate(d.getDate() + c.payment_terms_days)
      setDueDate(d.toISOString().split('T')[0])
    }
  }

  const patchLine = (key, patch) => setItems((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  const addLine = () => setItems((prev) => [...prev, newLine({ vat_rate_id: vatRates[0]?.id ?? null })])
  const removeLine = (key) => setItems((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev))

  const applyArticle = (article) => {
    if (!articleTarget) return
    patchLine(articleTarget, {
      description: article.description || article.code || '',
      unit_price: article.unit_price ?? 0,
      quantity: article.default_quantity ?? 1,
      vat_rate_id: article.vat_rate_id ?? vatRates[0]?.id ?? null,
    })
  }

  const buildPayload = (isDraft) => ({
    customer_id: Number(customerId),
    sender_company_id: currentCompanyId,
    issue_date: issueDate,
    due_date: isCreditNote ? issueDate : (dueDate || issueDate),
    is_draft: isDraft,
    is_credit_note: isCreditNote,
    is_peppol_sent: isPeppolSent,
    send_email_to_customer: sendEmail,
    attach_pdf: !isDraft,
    payment_method: isCreditNote ? null : paymentMethod,
    bank_account_id: isCreditNote || paymentMethod !== 'bank_transfer' ? null : (bankAccountId ? Number(bankAccountId) : null),
    use_structured_communication: !isCreditNote && paymentMethod === 'bank_transfer' ? useStructuredComm : false,
    payment_card_last_digits: !isCreditNote && paymentMethod === 'card' ? (cardLast4 || null) : null,
    payment_card_holder_name: !isCreditNote && paymentMethod === 'card' ? (cardHolder || null) : null,
    notes: isCreditNote ? null : (notes || null),
    billing_period_start_date: periodStart || null,
    billing_period_end_date: periodEnd || null,
    use_billing_address_for_delivery: useBillingForDelivery,
    delivery_address_id: useBillingForDelivery ? null : (deliveryAddressId ? Number(deliveryAddressId) : null),
    document_references: [
      ...(isCreditNote && seed?.document_references ? seed.document_references : []),
      ...docRefs.filter((r) => r.reference_type && r.reference_id.trim()).map((r) => ({ reference_type: r.reference_type, reference_id: r.reference_id.trim() })),
    ],
    items: items.map((l) => ({
      description: l.description.trim(),
      quantity: Number(l.quantity) || 0,
      unit_price: Number(l.unit_price) || 0,
      vat_rate_id: Number(l.vat_rate_id),
      note: l.note || null,
      allowance_charge_amount: l.allowance_charge_amount ? Number(l.allowance_charge_amount) : null,
      allowance_charge_reason: l.allowance_charge_reason || null,
      allowance_charge_is_percentage: !!l.allowance_charge_is_percentage,
      is_charge: !!l.is_charge,
      billing_period_start_date: l.period_start || null,
      billing_period_end_date: l.period_end || null,
      order_line_reference: l.order_ref || null,
      document_reference_id: l.doc_ref || null,
    })),
  })

  const validate = (isDraft) => {
    if (!customerId) return t('invoiceForm.validation.customerRequired')
    if (items.length === 0) return t('invoiceForm.validation.lineRequired')
    if (items.some((l) => !l.description.trim())) return t('invoiceForm.validation.descriptionRequired')
    if (!isDraft && !isCreditNote && paymentMethod === 'bank_transfer' && bankAccounts.length && !bankAccountId) return t('invoiceForm.validation.bankRequired')
    if (!isDraft && !isPeppolSent && !sendEmail) return t('invoiceForm.validation.deliveryRequired')
    return null
  }

  const save = async (isDraft) => {
    const err = validate(isDraft)
    if (err) { notify({ tone: 'error', title: t('invoiceForm.validation.title'), message: err }); return }
    setSubmitting(true)
    try {
      const { data } = isEdit
        ? await api.put(`/invoices/${id}`, buildPayload(isDraft))
        : await api.post('/invoices/', buildPayload(isDraft))
      const newId = data?.invoice?.id || data?.id || id
      toast(t(isDraft ? 'invoiceForm.toast.draftSaved' : 'invoiceForm.toast.finalized'), 'success')
      navigate(newId ? `/app/invoices/${newId}` : '/app/invoices')
    } catch (e) {
      notify({ tone: 'error', title: t('invoiceForm.validation.title'), message: e?.response?.data?.error || t('invoices.toast.error') })
    } finally {
      setSubmitting(false)
      setFinalize(null)
    }
  }

  // Finalize: fetch the next number, then confirm before committing.
  const openFinalize = async () => {
    const err = validate(false)
    if (err) { notify({ tone: 'error', title: t('invoiceForm.validation.title'), message: err }); return }
    let nextNumber = null
    try {
      const { data } = await api.get(`/companies/${currentCompanyId}/invoice-numbering/next`)
      nextNumber = data?.next_invoice_number
    } catch { /* number is informational only */ }
    setFinalize({ nextNumber })
  }

  const customerOptions = useMemo(
    () => customers.filter((c) => c.is_active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [customers],
  )

  const action = (
    <Button variant="secondary" icon="x" onClick={() => navigate('/app/invoices')}>{t('common.cancel')}</Button>
  )

  if (loading) {
    return <Page title={t('invoiceForm.titleNew')}><Card>{t('common.loading')}</Card></Page>
  }

  return (
    <Page title={isEdit ? t('invoiceForm.titleEdit') : (isCreditNote ? t('invoiceForm.titleCreditNote') : t('invoiceForm.titleNew'))} action={action} maxWidth={1080}>
      {/* Essential info */}
      <Card style={{ marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 16 }}>
          <Field label={t('invoiceForm.customer')} required>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select value={customerId} onChange={(e) => onCustomerChange(e.target.value)} style={{ flex: 1 }}>
                <option value="">{t('invoiceForm.selectCustomer')}</option>
                {customerOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
              <button type="button" title={t('invoiceForm.addCustomer')} aria-label={t('invoiceForm.addCustomer')} onClick={() => setShowCustomerModal(true)} style={{ width: 'var(--field-height, 44px)', height: 'var(--field-height, 44px)', flexShrink: 0, border: 'none', borderRadius: 'var(--radius-field)', background: 'var(--color-primary)', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="plus" size={18} /></button>
            </div>
          </Field>
          <Field label={t('invoiceForm.issueDate')} required>
            <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </Field>
          <Field label={t('invoiceForm.docType')}>
            <div style={{ display: 'flex', gap: 8, height: 'var(--field-height)', alignItems: 'center' }}>
              {[{ v: false, l: t('invoiceForm.invoice') }, { v: true, l: t('invoiceForm.creditNote') }].map((o) => (
                <button key={String(o.v)} type="button" onClick={() => setIsCreditNote(o.v)} style={{
                  flex: 1, height: 40, borderRadius: 'var(--radius-field)', fontSize: 13, fontWeight: 600,
                  border: '1.5px solid ' + (isCreditNote === o.v ? 'var(--color-primary)' : 'var(--color-border-strong)'),
                  background: isCreditNote === o.v ? 'var(--color-bg-lilac-strong)' : '#fff',
                  color: isCreditNote === o.v ? 'var(--color-primary)' : 'var(--color-text)',
                }}>{o.l}</button>
              ))}
            </div>
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 14, flexWrap: 'wrap' }}>
          <Check checked={isPeppolSent} onChange={setIsPeppolSent} label={t('invoiceForm.sendPeppol')} />
          <Check checked={sendEmail} onChange={setSendEmail} label={t('invoiceForm.sendEmail')} />
        </div>
      </Card>

      {/* Payment + misc sections — above the lines, per the handoff */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 18 }}>
        {!isCreditNote && (
          <Collapsible title={t('invoiceForm.paymentInfo')} icon="credit-card">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label={t('invoiceForm.paymentMethod')}>
                <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <option value="bank_transfer">{t('invoiceForm.bankTransfer')}</option>
                  <option value="card">{t('invoiceForm.card')}</option>
                </Select>
              </Field>
              {paymentMethod === 'bank_transfer' && (
                <Field label={t('invoiceForm.bankAccount')}>
                  <Select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
                    <option value="">{t('invoiceForm.selectBank')}</option>
                    {bankAccounts.map((a) => <option key={a.id} value={a.id}>{a.account_name} — {a.iban}{a.is_default ? ` (${t('parties.default')})` : ''}</option>)}
                  </Select>
                </Field>
              )}
            </div>
            {paymentMethod === 'card' && (
              <>
                <div style={{ background: 'var(--color-bg-lilac)', borderRadius: 10, padding: '9px 12px', fontSize: 12.5, color: 'var(--color-text)', marginBottom: 12 }}>{t('invoiceForm.cardPaidNote')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label={t('invoiceForm.cardLast4')}><Input value={cardLast4} onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" placeholder="1234" /></Field>
                  <Field label={t('invoiceForm.cardHolder')}><Input value={cardHolder} onChange={(e) => setCardHolder(e.target.value)} /></Field>
                </div>
              </>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
              <Field label={t('invoiceForm.notes')}>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
              </Field>
              {paymentMethod === 'bank_transfer' && (
                <Field label={t('invoiceForm.structuredComm')}>
                  <div style={{ height: 'var(--field-height)', display: 'flex', alignItems: 'center' }}>
                    <Check checked={useStructuredComm} onChange={setUseStructuredComm} label={t('invoiceForm.useStructuredComm')} />
                  </div>
                </Field>
              )}
            </div>

            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', margin: '6px 0 8px' }}>{t('invoiceForm.docRefs.title')}</div>
            {docRefs.map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <Select value={r.reference_type} onChange={(e) => setDocRefs((d) => d.map((x, j) => (j === i ? { ...x, reference_type: e.target.value } : x)))} style={{ width: 160 }}>
                  {REF_TYPES.map((rt) => <option key={rt} value={rt}>{t(`invoiceForm.docRefs.types.${rt}`)}</option>)}
                </Select>
                <Input value={r.reference_id} onChange={(e) => setDocRefs((d) => d.map((x, j) => (j === i ? { ...x, reference_id: e.target.value } : x)))} placeholder={t('invoiceForm.docRefs.value')} style={{ flex: 1 }} />
                <IconButton icon="trash" variant="danger" title={t('common.delete')} onClick={() => setDocRefs((d) => d.filter((_, j) => j !== i))} />
              </div>
            ))}
            <Button variant="secondary" icon="plus" onClick={() => setDocRefs((d) => [...d, { reference_type: 'ORDER', reference_id: '' }])}>{t('invoiceForm.docRefs.add')}</Button>
          </Collapsible>
        )}
        <Collapsible title={t('invoiceForm.miscInfo')} icon="settings">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
            <Field label={t('invoiceForm.dueDate')} hint={t('invoiceForm.dueDateHint')}>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} disabled={isCreditNote} />
            </Field>
            <Field label={t('invoiceForm.deliveryAddress')}>
              <div style={{ height: 'var(--field-height)', display: 'flex', alignItems: 'center' }}>
                <Check checked={useBillingForDelivery} onChange={setUseBillingForDelivery} label={t('invoiceForm.sameAsBilling')} />
              </div>
            </Field>
          </div>
          {!useBillingForDelivery && (
            <Field label={t('invoiceForm.deliveryAddress')}>
              <Select value={deliveryAddressId} onChange={(e) => setDeliveryAddressId(e.target.value)} disabled={!customerId}>
                <option value="">{customerId ? t('invoiceForm.selectDelivery') : t('invoiceForm.selectCustomerFirst')}</option>
                {deliveryAddresses.map((a) => <option key={a.id} value={a.id}>{[a.name, a.address, a.city].filter(Boolean).join(' · ')}</option>)}
              </Select>
            </Field>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label={t('invoiceForm.periodStart')}><Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} /></Field>
            <Field label={t('invoiceForm.periodEnd')}><Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} /></Field>
          </div>
        </Collapsible>
      </div>

      {/* Lines table */}
      <Card padding={0} style={{ marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
            <thead>
              <tr style={{ background: 'linear-gradient(90deg,#6D28D9,#7C3AED)', color: '#fff' }}>
                <th style={{ ...lineTh(), textAlign: 'left' }}>{t('invoiceForm.cols.description')}</th>
                <th style={lineTh(90)}>{t('invoiceForm.cols.qty')}</th>
                <th style={lineTh(120)}>{t('invoiceForm.cols.unitPrice')}</th>
                <th style={lineTh(110)}>{t('invoiceForm.cols.vat')}</th>
                <th style={lineTh(110)}>{t('invoiceForm.cols.vatAmount')}</th>
                <th style={lineTh(120)}>{t('invoiceForm.cols.total')}</th>
                <th style={lineTh(44)} />
              </tr>
            </thead>
            <tbody>
              {items.map((l) => {
                const lt = lineTotals(l, ratePctOf(l))
                return (
                  <tr key={l.key} style={{ borderBottom: '1px solid var(--color-border-row)', verticalAlign: 'top' }}>
                    <td style={lineTd}>
                      {/* Single row (mockup): toggle icons · description · search */}
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        <MiniAction icon="note" title={t('invoiceForm.line.note')} active={l.showNote} onClick={() => patchLine(l.key, { showNote: !l.showNote, note: l.showNote ? '' : l.note })} />
                        <MiniAction glyph="%" title={t('invoiceForm.line.discount')} active={l.showAllowance} onClick={() => patchLine(l.key, { showAllowance: !l.showAllowance, allowance_charge_amount: l.showAllowance ? '' : l.allowance_charge_amount })} />
                        <MiniAction icon="calendar" title={t('invoiceForm.line.period')} active={l.showPeriod} onClick={() => patchLine(l.key, { showPeriod: !l.showPeriod, period_start: l.showPeriod ? '' : l.period_start, period_end: l.showPeriod ? '' : l.period_end })} />
                        <MiniAction glyph="#" title={t('invoiceForm.line.reference')} active={l.showRef} onClick={() => patchLine(l.key, { showRef: !l.showRef, order_ref: l.showRef ? '' : l.order_ref, doc_ref: l.showRef ? '' : l.doc_ref })} />
                        <Input value={l.description} onChange={(e) => patchLine(l.key, { description: e.target.value })} placeholder={t('invoiceForm.cols.description')} style={{ height: 40, flex: 1, minWidth: 80 }} />
                        <button type="button" title={t('invoiceForm.line.article')} aria-label={t('invoiceForm.line.article')} onClick={() => setArticleTarget(l.key)} style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 9, border: '1.5px solid var(--color-border-alt)', background: 'var(--color-bg-lilac)', color: 'var(--color-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Icon name="search" size={16} /></button>
                      </div>
                      {l.showNote && <Textarea value={l.note} onChange={(e) => patchLine(l.key, { note: e.target.value })} placeholder={t('invoiceForm.line.note')} rows={2} style={{ marginTop: 6 }} />}
                      {l.showAllowance && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Input type="number" step="0.01" value={l.allowance_charge_amount} onChange={(e) => patchLine(l.key, { allowance_charge_amount: e.target.value })} placeholder={t('invoiceForm.line.amount')} style={{ height: 38, width: 110 }} />
                          <button type="button" onClick={() => patchLine(l.key, { allowance_charge_is_percentage: !l.allowance_charge_is_percentage })} style={toggleBtn}>{l.allowance_charge_is_percentage ? '%' : '€'}</button>
                          <button type="button" onClick={() => patchLine(l.key, { is_charge: !l.is_charge })} style={{ ...toggleBtn, width: 'auto', padding: '0 12px', color: l.is_charge ? 'var(--color-info)' : 'var(--color-success)' }}>
                            {l.is_charge ? t('invoiceForm.line.charge') : t('invoiceForm.line.discountLabel')}
                          </button>
                          <Input value={l.allowance_charge_reason} onChange={(e) => patchLine(l.key, { allowance_charge_reason: e.target.value })} placeholder={t('invoiceForm.line.reason')} style={{ height: 38, flex: 1, minWidth: 120 }} />
                        </div>
                      )}
                      {l.showPeriod && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('invoiceForm.line.periodFrom')}</span>
                          <Input type="date" value={l.period_start} onChange={(e) => patchLine(l.key, { period_start: e.target.value })} style={{ height: 38, width: 160 }} />
                          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('invoiceForm.line.periodTo')}</span>
                          <Input type="date" value={l.period_end} onChange={(e) => patchLine(l.key, { period_end: e.target.value })} style={{ height: 38, width: 160 }} />
                        </div>
                      )}
                      {l.showRef && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
                          <div>
                            <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginBottom: 3 }}>{t('invoiceForm.line.orderRef')}</div>
                            <Input value={l.order_ref} onChange={(e) => patchLine(l.key, { order_ref: e.target.value })} placeholder={t('invoiceForm.line.orderRefPlaceholder')} style={{ height: 38 }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11.5, color: 'var(--color-text-muted)', marginBottom: 3 }}>{t('invoiceForm.line.docRef')}</div>
                            <Input value={l.doc_ref} onChange={(e) => patchLine(l.key, { doc_ref: e.target.value })} placeholder={t('invoiceForm.line.docRefPlaceholder')} style={{ height: 38 }} />
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={lineTd}><Input type="number" step="0.01" min="0" value={l.quantity} onChange={(e) => patchLine(l.key, { quantity: e.target.value })} style={cellInput} /></td>
                    <td style={lineTd}><Input type="number" step="0.01" min="0" value={l.unit_price} onChange={(e) => patchLine(l.key, { unit_price: e.target.value })} style={cellInput} /></td>
                    <td style={lineTd}>
                      <Select value={l.vat_rate_id ?? ''} disabled={nonVatLiable} onChange={(e) => patchLine(l.key, { vat_rate_id: Number(e.target.value) })} style={{ height: 40 }}>
                        {vatRates.map((v) => <option key={v.id} value={v.id} title={v.name}>{v.rate}%</option>)}
                      </Select>
                    </td>
                    <td style={{ ...lineTd, textAlign: 'right', fontFamily: 'var(--font-title)', whiteSpace: 'nowrap', paddingTop: 18 }}>{formatCurrency(lt.vat)}</td>
                    <td style={{ ...lineTd, textAlign: 'right', fontFamily: 'var(--font-title)', fontWeight: 600, whiteSpace: 'nowrap', paddingTop: 18 }}>{formatCurrency(lt.ttc)}</td>
                    <td style={lineTd}>
                      <IconButton icon="trash" variant="danger" title={t('common.delete')} disabled={items.length <= 1} onClick={() => removeLine(l.key)} style={{ marginTop: 2 }} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '12px 16px' }}>
          <Button variant="secondary" icon="plus" onClick={addLine}>{t('invoiceForm.addLine')}</Button>
        </div>
      </Card>

      {isEdit && (
        <Card style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: attachments.length ? 12 : 0 }}>
            <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15 }}>{t('invoiceForm.attachments.title')}</h3>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40, padding: '0 16px', borderRadius: 'var(--radius-field)', border: '1.5px solid var(--color-border-alt)', background: '#fff', color: 'var(--color-ink)', fontSize: 14, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}>
              <Icon name="download" size={17} /> {t('invoiceForm.attachments.upload')}
              <input type="file" onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = '' }} style={{ display: 'none' }} disabled={uploading} />
            </label>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {attachments.map((att) => (
              <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', border: '1px solid var(--color-border)', borderRadius: 10, fontSize: 13.5 }}>
                <Icon name={att.is_ubl_xml ? 'link' : 'invoice'} size={16} />
                <span style={{ flex: 1, color: 'var(--color-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.filename}</span>
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{Math.round((att.file_size || 0) / 1024)} Ko</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Totals (full width, below the lines) + actions */}
      <Card>
        <div style={{ maxWidth: 420, marginLeft: 'auto' }}>
          <Row label={t('invoiceForm.subtotal')} value={formatCurrency(totals.totalHt)} />
          {totals.totalAllowances > 0 && <Row label={t('invoiceForm.discounts')} value={`− ${formatCurrency(totals.totalAllowances)}`} color="var(--color-danger)" />}
          {totals.totalCharges > 0 && <Row label={t('invoiceForm.charges')} value={`+ ${formatCurrency(totals.totalCharges)}`} color="var(--color-info)" />}
          {totals.vatBreakdown.filter((b) => b.vat > 0).map((b) => (
            <Row key={b.rate} label={`${t('invoiceForm.vat')} ${b.rate}%`} value={formatCurrency(b.vat)} muted />
          ))}
          <Row label={t('invoiceForm.totalVat')} value={formatCurrency(totals.totalVat)} />
          <div style={{ height: 1, background: 'var(--color-border)', margin: '12px 0' }} />
          <Row label={t('invoiceForm.totalTtc')} value={formatCurrency(totals.totalTtc)} big />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
          <Button variant="secondary" icon="note" disabled={submitting} onClick={() => save(true)}>{t('invoiceForm.saveDraft')}</Button>
          <Button variant="primary" icon="check" loading={submitting} onClick={openFinalize}>{t('invoiceForm.finalize')}</Button>
        </div>
      </Card>

      <ArticleSearchModal open={articleTarget != null} onClose={() => setArticleTarget(null)} onSelect={applyArticle} />

      {/* Quick-add a customer without leaving the invoice (handoff: + next to CLIENT). */}
      <PartyFormModal
        open={showCustomerModal} config={PARTY_CONFIG.customers}
        onClose={() => setShowCustomerModal(false)}
        onSaved={(cust) => {
          setShowCustomerModal(false)
          if (cust?.id) { setCustomers((cs) => [...cs.filter((c) => c.id !== cust.id), cust]); onCustomerChange(String(cust.id)) }
          toast(t('parties.toast.saved'), 'success')
        }}
      />

      {/* Finalize confirmation */}
      <Modal open={!!finalize} onClose={() => setFinalize(null)} maxWidth={440}>
        <div style={{ padding: 26 }}>
          <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{t('invoiceForm.finalizeConfirm.title')}</h3>
          <p style={{ color: 'var(--color-text)', marginBottom: 16 }}>{t('invoiceForm.finalizeConfirm.message')}</p>
          <div style={{ background: 'var(--color-bg-lilac)', borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
            {finalize?.nextNumber && <Row label={t('invoiceForm.finalizeConfirm.number')} value={finalize.nextNumber} />}
            <Row label={t('invoiceForm.totalTtc')} value={formatCurrency(totals.totalTtc)} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button variant="secondary" onClick={() => setFinalize(null)}>{t('common.cancel')}</Button>
            <Button variant="primary" icon="check" loading={submitting} onClick={() => save(false)}>{t('invoiceForm.finalize')}</Button>
          </div>
        </div>
      </Modal>
    </Page>
  )
}

/* ---- small presentational helpers ---- */
const lineTh = (w) => ({ padding: '12px 12px', fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em', width: w, textAlign: w && w < 130 ? 'center' : 'left', whiteSpace: 'nowrap' })
const lineTd = { padding: '10px 12px' }
const cellInput = { height: 40, textAlign: 'right' }
const toggleBtn = { width: 40, height: 38, borderRadius: 9, border: '1.5px solid var(--color-border-strong)', background: '#fff', fontWeight: 700, color: 'var(--color-text)' }

function MiniAction({ icon, glyph, title, active, onClick }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} style={{
      width: 30, height: 30, flexShrink: 0, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      border: '1.5px solid ' + (active ? 'var(--color-primary)' : 'var(--color-border-alt)'),
      background: active ? 'var(--color-bg-lilac-strong)' : '#fff', color: active ? 'var(--color-primary)' : 'var(--color-text-muted)',
      fontSize: 14, fontWeight: 700, cursor: 'pointer',
    }}>
      {glyph || <Icon name={icon} size={15} />}
    </button>
  )
}

function Check({ checked, onChange, label }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 14, color: 'var(--color-ink)', fontWeight: 500 }}>
      <span onClick={() => onChange(!checked)} style={{
        width: 20, height: 20, borderRadius: 6, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: '1.5px solid ' + (checked ? 'var(--color-primary)' : 'var(--color-border-strong)'),
        background: checked ? 'var(--color-primary)' : '#fff', color: '#fff',
      }}>{checked && <Icon name="check" size={14} />}</span>
      {label}
    </label>
  )
}

function Row({ label, value, big, muted, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: big ? '2px 0' : '3px 0' }}>
      <span style={{ fontSize: big ? 15 : 13.5, color: muted ? 'var(--color-text-muted)' : 'var(--color-text)', fontWeight: big ? 700 : 500 }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-title)', fontSize: big ? 22 : 14, fontWeight: big ? 800 : 600, color: color || (big ? 'var(--color-success)' : 'var(--color-ink)') }}>{value}</span>
    </div>
  )
}
